import './baplieOrRestow.less'
import { Alert, Card as AntCard, Button, Spin } from 'antd'
import BAPLIE_SYNC_STATUSES, {
  BAPLIE_SYNC_STATUS_KEYS,
} from 'constants/baplieSyncStatuses'
import React, { useState } from 'react'
import BAPLIE_FILE_TYPES from 'constants/baplieFileTypes'
import BaplieOrRestowHistoryDrawer from './BaplieOrRestowHistoryDrawer'
import BaplieOrRestowUploadModal from './BaplieOrRestowUploadModal'
import Card from 'components/Card'
import featureCodes from 'constants/featureCodes'
import { formatBackendDateTimeNaive } from 'global/util/time'
import getDeviceSyncStatusIcon from './getDeviceSyncStatusIcon'
import Icon from '@ant-design/icons'
import MoreInfoContent from './MoreInfoContent'
import NoData from 'components/NoData'
import PropTypes from 'prop-types'
import Text from 'components/Text'
import useFeatureAccessControlEnabled from 'hooks/useFeatureAccessControlEnabled'
import userRoles from 'constants/userRoles'
import { useTranslation } from 'react-i18next'

import { ReactComponent as DischargeContainer } from 'assets/icons/discharge-container.svg'
import { ReactComponent as LoadContainer } from 'assets/icons/load-container.svg'
import { ReactComponent as RestowContainer } from 'assets/icons/restows.svg'

const BaplieOrRestow = ({
  baplieData,
  hasError,
  isLoading,
  isDownloading,
  isUploading,
  isPortCallClosed,
  onDownload,
  onUpload,
  uploadError,
  portCallBaplieRestowUploadFailed,
  userRole,
}) => {
  const { t } = useTranslation()
  const isBaplieRestowUploadEnabled = useFeatureAccessControlEnabled(
    featureCodes.UPLOAD_BAPLIE_OR_RESTOW
  )

  // Show Baplie/Restow upload feature only if port call open and user is a ship role
  const canShowUploadButton =
    isBaplieRestowUploadEnabled &&
    !isPortCallClosed &&
    userRole === userRoles.SHIP
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false)
  const [uploadModalType, setUploadModalType] = useState(null)

  const openUploadDrawer = () => setUploadDrawerOpen(true)
  const closeUploadDrawer = () => setUploadDrawerOpen(false)
  const openUploadModal = fileType => {
    setUploadModalType(fileType)
  }
  const closeUploadModal = () => {
    setUploadModalType(null)
    if (portCallBaplieRestowUploadFailed) {
      portCallBaplieRestowUploadFailed(null)
    }
  }

  // Find the latest file for each type based on uploadedTime
  const getLatestFileByType = type => {
    const filesOfType = baplieData?.filter(file => file.type === type) || []

    if (filesOfType.length === 0) return null

    // Sort by uploadedTime in descending order and return the most recent
    return filesOfType.sort(
      (a, b) => new Date(b.uploadedTime) - new Date(a.uploadedTime)
    )[0]
  }

  const loadData = getLatestFileByType(BAPLIE_FILE_TYPES.LOAD)
  const dischargeData = getLatestFileByType(BAPLIE_FILE_TYPES.DISCHARGE)
  const restowData = getLatestFileByType(BAPLIE_FILE_TYPES.RESTOW)

  const renderCard = (label, fileData, fileType) => {
    const fileName = fileData?.filename
    const devices = fileData?.devices || []
    const hasFileName = !!fileName

    const getCardIcon = () => {
      switch (fileType) {
        case BAPLIE_FILE_TYPES.DISCHARGE:
          return (
            <Icon
              className="baplie-or-restow__card-title-icon"
              component={DischargeContainer}
            />
          )
        case BAPLIE_FILE_TYPES.LOAD:
          return (
            <Icon
              className="baplie-or-restow__card-title-icon"
              component={LoadContainer}
            />
          )
        case BAPLIE_FILE_TYPES.RESTOW:
        default:
          return (
            <Icon
              className="baplie-or-restow__card-title-icon"
              component={RestowContainer}
            />
          )
      }
    }

    const cardClassName = `baplie-or-restow__card ${
      fileType === BAPLIE_FILE_TYPES.DISCHARGE
        ? 'baplie-or-restow__card--discharge'
        : fileType === BAPLIE_FILE_TYPES.LOAD
          ? 'baplie-or-restow__card--load'
          : 'baplie-or-restow__card--restow'
    }`

    return (
      <AntCard
        bordered={false}
        className={cardClassName}
        extra={
          canShowUploadButton && (
            <Button
              className="baplie-or-restow__upload-button"
              disabled={isLoading || isUploading}
              onClick={() => openUploadModal(fileType)}
              size="small"
              type="primary"
            >
              {t('portCall.shipOverview.baplieOrRestowInfo.upload')}
            </Button>
          )
        }
        title={
          <div className="baplie-or-restow__card-title">
            {getCardIcon()}
            <Text className="baplie-or-restow__card-title-text">{label}</Text>
          </div>
        }
      >
        <div className="baplie-or-restow__card-body">
          {hasFileName ? (
            <>
              <div className="baplie-or-restow__row">
                <div className="baplie-or-restow__row-content baplie-or-restow__row-content--file-name">
                  <Text className="baplie-or-restow__row-label baplie-or-restow__file-name-label">
                    {t(
                      'portCall.shipOverview.baplieOrRestowInfo.fileNameLabel'
                    )}
                  </Text>
                  <Text className="baplie-or-restow__file-name-value">
                    {fileName}
                  </Text>
                </div>
              </div>

              {devices.length > 0 ? (
                devices.map(device => {
                  const statusKey = BAPLIE_SYNC_STATUS_KEYS[device.syncStatus]
                  const statusLabel = statusKey
                    ? t(statusKey)
                    : t('portCall.shipOverview.baplieOrRestowInfo.unknown')
                  const statusClass = statusLabel
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                  const isCompleted =
                    device.syncStatus === BAPLIE_SYNC_STATUSES.COMPLETED
                  const syncCompletedAt =
                    isCompleted && device.syncAt
                      ? formatBackendDateTimeNaive(device.syncAt)
                      : null

                  const StatusIcon = getDeviceSyncStatusIcon(device.syncStatus)

                  return (
                    <div
                      className="baplie-or-restow__row"
                      key={device.deviceid}
                    >
                      <div className="baplie-or-restow__row-content">
                        <Text className="baplie-or-restow__row-label">
                          {`${device.deviceName}:`}
                        </Text>
                        <div
                          className={`baplie-or-restow__device-status-badge baplie-or-restow__device-status-badge--${statusClass}`}
                        >
                          {StatusIcon && <StatusIcon />}
                          <Text className="baplie-or-restow__device-status-text">
                            {statusLabel}
                          </Text>
                        </div>
                        {syncCompletedAt && (
                          <>
                            <Text className="baplie-or-restow__device-divider">
                              |
                            </Text>
                            <Text>{syncCompletedAt}</Text>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="baplie-or-restow__row">
                  <div className="baplie-or-restow__row-content">
                    <Text className="baplie-or-restow__row-label">
                      {`${t(
                        'portCall.shipOverview.baplieOrRestowInfo.deviceLabel'
                      )}:`}
                    </Text>
                    <Text>{t('portCall.shipOverview.noDeviceStatus')}</Text>
                  </div>
                </div>
              )}
            </>
          ) : (
            <NoData />
          )}
        </div>
      </AntCard>
    )
  }

  return (
    <>
      <Card
        className="baplie-or-restow-card"
        id="baplie-or-restow"
        moreInfoTitle={t('portCall.shipOverview.baplieOrRestowMoreInfo')}
        title={t('portCall.shipOverview.baplieOrRestow')}
        headerActionButton={{
          disabled: false,
          label: t('portCall.shipOverview.viewHistory'),
          onClick: openUploadDrawer,
        }}
        moreInfoContent={
          <MoreInfoContent canShowUploadInfo={canShowUploadButton} />
        }
      >
        {isLoading ? (
          <div className="baplie-or-restow__loading-spin">
            <Spin />
          </div>
        ) : (
          <>
            {hasError && (
              <div className="baplie-or-restow__error">
                <Alert
                  banner
                  showIcon
                  type="error"
                  message={t(
                    'portCall.shipOverview.baplieOrRestowInfo.historyStaleWarning'
                  )}
                />
              </div>
            )}
            <div className="baplie-or-restow__container">
              {renderCard(
                t('portCall.shipOverview.discharge'),
                dischargeData,
                BAPLIE_FILE_TYPES.DISCHARGE
              )}
              {renderCard(
                t('portCall.shipOverview.load'),
                loadData,
                BAPLIE_FILE_TYPES.LOAD
              )}
              {renderCard(
                t('portCall.shipOverview.restow'),
                restowData,
                BAPLIE_FILE_TYPES.RESTOW
              )}
            </div>
            <Alert
              className="baplie-or-restow__note"
              type="info"
              message={t(
                'portCall.shipOverview.baplieOrRestowInfo.historyDashboardNote'
              )}
            />
          </>
        )}
      </Card>

      <BaplieOrRestowHistoryDrawer
        baplieData={baplieData}
        isDownloading={isDownloading}
        isOpen={uploadDrawerOpen}
        onClose={closeUploadDrawer}
        onDownload={onDownload}
      />

      <BaplieOrRestowUploadModal
        fileType={uploadModalType}
        isOpen={!!uploadModalType}
        isUploading={isUploading}
        onClose={closeUploadModal}
        onUpload={onUpload}
        t={t}
        uploadError={uploadError}
      />
    </>
  )
}

BaplieOrRestow.defaultProps = {
  baplieData: null,
  hasError: false,
  isDownloading: false,
  isPortCallClosed: false,
  isUploading: false,
  onDownload: null,
  onUpload: null,
  portCallBaplieRestowUploadFailed: null,
  uploadError: null,
  userRole: null,
}

BaplieOrRestow.propTypes = {
  baplieData: PropTypes.arrayOf(
    PropTypes.shape({
      devices: PropTypes.arrayOf(
        PropTypes.shape({
          deviceName: PropTypes.string.isRequired,
          deviceid: PropTypes.string.isRequired,
          syncAt: PropTypes.string,
          syncStatus: PropTypes.oneOf(Object.values(BAPLIE_SYNC_STATUSES))
            .isRequired,
        })
      ).isRequired,
      filename: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(Object.values(BAPLIE_FILE_TYPES)).isRequired,
      uploadedTime: PropTypes.string.isRequired,
    })
  ),
  hasError: PropTypes.bool,
  isDownloading: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  isPortCallClosed: PropTypes.bool,
  isUploading: PropTypes.bool,
  onDownload: PropTypes.func,
  onUpload: PropTypes.func,
  portCallBaplieRestowUploadFailed: PropTypes.func,
  uploadError: PropTypes.string,
  userRole: PropTypes.string,
}

export default BaplieOrRestow
